import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {
	getAuthToken,
	getInreesId,
	getInreesToken,
	getUser,
	setAuthToken,
	setInreesId,
	setInreesToken,
	setShowSnackbar,
	setUser,
} from "../store/reducers/authSlice";
import {useLazyGetAuthKeyQuery, useLazyGetUserQuery} from "../store/api/user";
import {useCookie, useCookieWatcher} from "@fcannizzaro/react-use-cookie-watcher";
import Cookies from "universal-cookie";

export const useIsLoggedIn = () => {
	const [isLoggedIn, setIsLoggedIn] = React.useState(null);

	const inreesId = useSelector(getInreesId);
	const inreesToken = useSelector(getInreesToken);

	const inexIDExists = useCookieWatcher("INEXPLORE_ID", {
		checkEvery: 1000,
		valueOnly: true,
	});
	const inexID = useCookie("INEXPLORE_ID");
	const inexTokenExists = useCookieWatcher("INEXPLORE_TOKEN", {
		checkEvery: 1000,
		valueOnly: true,
	});
	const inexToken = useCookie("INEXPLORE_TOKEN");

	const dispatch = useDispatch();
	React.useEffect(() => {
		if (inexIDExists && inexTokenExists) {
			if (inexID !== inreesId || inexToken !== inreesToken) {
				dispatch(setInreesId(inexID));
				dispatch(setInreesToken(inexToken));
				dispatch(setShowSnackbar(true));
			}
			setIsLoggedIn(true);
		} else {
			setIsLoggedIn(false);
			dispatch(setInreesId(null));
			dispatch(setInreesToken(null));
			dispatch(setShowSnackbar(false));
		}
	}, [inreesId, inreesToken, inexID, inexToken, inexIDExists, inexTokenExists]);

	return isLoggedIn;
};

export const useLogUserIn = () => {
	const authToken = useSelector(getAuthToken);
	const user = useSelector(getUser);

	const inreesId = useSelector(getInreesId);
	const inreesToken = useSelector(getInreesToken);
	const affiliation = useCookie("affiliation");
	const tracking = useCookie("tracking");

	const [fetchAuthKey, { isFetching: isFetchingAuthKey }] = useLazyGetAuthKeyQuery();
	const [fetchUser, { isError: isUserError, error: userError }] = useLazyGetUserQuery();

	const dispatch = useDispatch();
	React.useEffect(() => {
		if (inreesId && inreesToken && !authToken) {
			const now = new Date().getTime();
			fetchAuthKey({ inreesId, inreesToken, affiliation, tracking, ts: now })
			.unwrap()
			.then((data) => {
				if (data?.cle) {
					const cle = data?.cle;
					dispatch(setAuthToken(cle));
				} 
			})
			.catch((err1) => {
				console.error({id: inreesId, err1})
			});
		} 
	}, [inreesId, inreesToken, authToken]);

	React.useEffect(() => {
		if (authToken && !user) {
			const now = new Date().getTime();
			fetchUser({ts: now})
			.unwrap()
			.then((data) => {
				if(data && data?.idinexplore) {
					dispatch(setUser(data));
				}
			})
			.catch((errAuth) => {
				// En cas d'erreur, on réinitialise le token pour relancer la génération
				dispatch(setUser(null));
				dispatch(setAuthToken(null));
				console.log({id: user?.id, errAuth});
			});
		}
	}, [authToken]);
};

export const getLoginRedirectURL = () => {
	return `${
		process.env.NODE_ENV === "development" ? "http://localhost:3001" : "https://login.inexplore.com/"
	}/?referrer=https://praticiens.inexplore.com/`;
};

export const useRedirectToLogin = () => {
	const isLoggedIn = useIsLoggedIn();

	React.useEffect(() => {
		const redirectUrl = getLoginRedirectURL();
		if (isLoggedIn === false) window.location.replace(redirectUrl);
	}, [isLoggedIn]);
};

export const useAuthToken = () => {
	const authToken = useSelector(getAuthToken);

	return React.useMemo(() => authToken, [authToken]);
};

export const useLogUserOut = () => {
	const dispatch = useDispatch();

	return React.useCallback(() => {
		const cookies = new Cookies();
		cookies.remove("INEXPLORE_USER", {
			path: "/",
			domain: process.env.NODE_ENV !== "development" ? ".inexplore.com" : "localhost",
		});
		cookies.remove("INEXPLORE_TOKEN", {
			path: "/",
			domain: process.env.NODE_ENV !== "development" ? ".inexplore.com" : "localhost",
		});
		cookies.remove("INEXPLORE_ID", {
			path: "/",
			domain: process.env.NODE_ENV !== "development" ? ".inexplore.com" : "localhost",
		});
		dispatch(setInreesId(null));
		dispatch(setInreesToken(null));
		dispatch(setAuthToken(null));
	}, []);
};

export const useIsSubscribed = () => {
	const user = useSelector(getUser);
	const loggedIn = useIsLoggedIn();

	return React.useMemo(() => {
		if (loggedIn === false) return false;
		if (!user) return null;
		const datenow = Math.floor(Date.now() / 1000);
		return parseInt(user?.praticien) > datenow;
	}, [loggedIn, user]);
}

export const useRedirectIfNotSubscribed = () => {
	useLogUserIn();

	const isSubscribed = useIsSubscribed();

	React.useEffect(() => {
		if (isSubscribed === false) {
			window.location = "/public";
		}
	}, [isSubscribed]);
}
